"use client"


import { useState } from "react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAuth } from "../contexts/AuthContext"
import { useNavigate } from "react-router-dom"

const amplyLogo = new URL('../assets/amply.png', import.meta.url).href


const Login = () => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)
    const { data, error } = await signIn({ email, password })
    if (error) {
      setError(error.message)
      setIsLoading(false)
      return
    }
    // On successful login, navigate to dashboard
    setIsLoading(false)
    navigate("/dashboard")
  }

  return (
    <>
       <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <a
        href="/"
  className="absolute top-6 left-6 z-20 text-zinc-400 hover:text-[#e3c5b3] transition-colors duration-200 flex items-center space-x-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span>Back to Home</span>
      </a>

      {/* Background gradient */}
      <div style={{ background: "radial-gradient(ellipse 50% 35% at 50% 0%, rgba(0,0,0,0.8), rgba(255,255,255,0.1) 60%), #000", }} className="absolute inset-0" />

      {/* Decorative elements */}
  <div style={{ background: "radial-gradient(ellipse 50% 35% at 50% 0%, rgba(0,0,0,0.8), rgba(255,255,255,0.1) 60%), #000", }} className="absolute top-20 left-20 w-72 h-72 rounded-full blur-3xl" />
  <div style={{ background: "radial-gradient(ellipse 50% 35% at 50% 0%, rgba(0,0,0,0.8), rgba(255,255,255,0.1) 60%), #000", }} className="absolute bottom-20 right-20 w-96 h-96 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <a href="/" className="inline-block mb-6">
            <div className="flex items-center justify-center space-x-2">
              <span className="rounded-full bg-[#a78bfa]/10 p-2">
                <img 
                src={amplyLogo}
                className="w-8 h-8"
                alt="Amply Logo"
                />
              </span>
            </div>
          </a>
          <h1 className="text-3xl font-bold text-white mb-2">Welcome back</h1>
          <p className="text-zinc-400">Sign in to your account to continue</p>
        </div>

        {/* Login Form */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 rounded-2xl p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-[#a78bfa] focus:ring-[#a78bfa]/20"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-[#a78bfa] focus:ring-[#a78bfa]/20"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#e3c5b3] hover:bg-[#e3c5b3]/90 text-white font-medium py-3 rounded-xl transition-colors"
            >
              {isLoading ? "Signing in..." : "Sign in"}
            </Button>
            {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-400">
              Don't have an account?{" "}
              <a href="/register" className="text-[#e3c5b3] hover:text-[#e3c5b3]/80 font-medium">
                Sign up
              </a>
            </p>
          </div>
        </motion.div>

        {/* Social Login 
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-6"
        >
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-800" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-transparent backdrop-blur-sm rounded text-zinc-500">Or continue with</span>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-2">
            <Button
              variant="outline"
              className="bg-zinc-900/50 border-zinc-800 text-zinc-300 hover:bg-white hover:text-black hover:border-white transition-all duration-200 group"
            >
              <svg
                className="w-5 h-5 mr-2 text-zinc-300 group-hover:text-black transition-colors duration-200"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Google
            </Button>
           
          </div>
        </motion.div>*/}
      </motion.div>
    </div>
    </>
  )
}

export default Login;
